import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Bell,
  CheckCircle,
  Copy,
  Edit,
  Info,
  Plus,
  Save,
  Search,
  Trash2,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Input, TextArea } from '../components/forms';
import LoadingSpinner from '../components/LoadingSpinner';

interface NotificationTemplate {
  id: string;
  name: string;
  title: string;
  message: string;
  category: string;
  type: 'info' | 'success' | 'warning' | 'error';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  description?: string;
  variables: string[];
  actionButtons: ActionButton[];
  deliverySettings: DeliverySettings;
  isActive: boolean;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
  usageCount: number;
}

interface ActionButton {
  id: string;
  label: string;
  action: 'navigate' | 'api' | 'dismiss';
  value: string;
  style: 'primary' | 'secondary' | 'danger';
}

interface DeliverySettings {
  channels: ('in-app' | 'push' | 'email')[];
  timing: 'immediate' | 'delayed' | 'scheduled';
  delayMinutes?: number;
  scheduleTime?: string;
  targetAudience: 'all' | 'specific' | 'role-based';
  expiresAfterHours?: number;
  allowDismiss: boolean;
  soundEnabled: boolean;
}

interface TemplateVariable {
  name: string;
  description: string;
  example: string;
}

const NotificationTemplates: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<NotificationTemplate | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editMode, setEditMode] = useState<'create' | 'edit'>('create');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Editor state
  const [templateData, setTemplateData] = useState({
    name: '',
    title: '',
    message: '',
    category: '',
    type: 'info' as 'info' | 'success' | 'warning' | 'error',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    description: '',
    variables: [] as string[],
    actionButtons: [] as ActionButton[],
    deliverySettings: {
      channels: ['in-app'] as ('in-app' | 'push' | 'email')[],
      timing: 'immediate' as 'immediate' | 'delayed' | 'scheduled',
      targetAudience: 'all' as 'all' | 'specific' | 'role-based',
      allowDismiss: true,
      soundEnabled: false,
      expiresAfterHours: 24,
    } as DeliverySettings,
  });

  const categories = [
    { value: 'project-updates', label: 'Project Updates' },
    { value: 'task-management', label: 'Task Management' },
    { value: 'team-collaboration', label: 'Team Collaboration' },
    { value: 'safety-alerts', label: 'Safety Alerts' },
    { value: 'system-messages', label: 'System Messages' },
    { value: 'deadlines', label: 'Deadlines & Reminders' },
    { value: 'approvals', label: 'Approvals & Reviews' },
    { value: 'reports', label: 'Reports & Analytics' },
  ];

  const notificationTypes = [
    { value: 'info', label: 'Information', icon: Info, color: 'blue' },
    { value: 'success', label: 'Success', icon: CheckCircle, color: 'green' },
    { value: 'warning', label: 'Warning', icon: AlertTriangle, color: 'orange' },
    { value: 'error', label: 'Error', icon: AlertCircle, color: 'red' },
  ];

  const priorityLevels = [
    { value: 'low', label: 'Low Priority', color: 'gray' },
    { value: 'medium', label: 'Medium Priority', color: 'blue' },
    { value: 'high', label: 'High Priority', color: 'orange' },
    { value: 'urgent', label: 'Urgent', color: 'red' },
  ];

  const availableVariables: TemplateVariable[] = [
    { name: '{{user.firstName}}', description: "User's first name", example: 'John' },
    { name: '{{user.lastName}}', description: "User's last name", example: 'Doe' },
    { name: '{{user.role}}', description: "User's role", example: 'Project Manager' },
    { name: '{{project.name}}', description: 'Project name', example: 'Downtown Office Building' },
    { name: '{{project.code}}', description: 'Project code', example: 'DOB-2024' },
    { name: '{{task.name}}', description: 'Task name', example: 'Foundation Review' },
    { name: '{{task.dueDate}}', description: 'Task due date', example: '2024-01-20' },
    { name: '{{company.name}}', description: 'Company name', example: 'BuilderBel Construction' },
    { name: '{{date.current}}', description: 'Current date', example: '2024-01-15' },
    { name: '{{time.current}}', description: 'Current time', example: '14:30' },
    { name: '{{meeting.time}}', description: 'Meeting time', example: '2:30 PM' },
    { name: '{{deadline.date}}', description: 'Deadline date', example: '2024-01-25' },
  ];

  const sampleTemplates: NotificationTemplate[] = [
    {
      id: '1',
      name: 'Task Assignment',
      title: 'New Task Assigned',
      message:
        'You have been assigned a new task: {{task.name}} for project {{project.name}}. Due date: {{task.dueDate}}',
      category: 'task-management',
      type: 'info',
      priority: 'medium',
      description: 'Notification when a user is assigned a new task',
      variables: ['{{task.name}}', '{{project.name}}', '{{task.dueDate}}'],
      actionButtons: [
        {
          id: '1',
          label: 'View Task',
          action: 'navigate',
          value: '/tasks/{{task.id}}',
          style: 'primary',
        },
        { id: '2', label: 'Dismiss', action: 'dismiss', value: '', style: 'secondary' },
      ],
      deliverySettings: {
        channels: ['in-app', 'push'],
        timing: 'immediate',
        targetAudience: 'specific',
        allowDismiss: true,
        soundEnabled: true,
        expiresAfterHours: 48,
      },
      isActive: true,
      isSystem: true,
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-01T00:00:00Z',
      usageCount: 425,
    },
    {
      id: '2',
      name: 'Safety Alert',
      title: 'SAFETY ALERT: {{project.name}}',
      message:
        'URGENT: Safety incident reported at {{project.name}}. All personnel must follow emergency procedures immediately.',
      category: 'safety-alerts',
      type: 'error',
      priority: 'urgent',
      description: 'Critical safety alert for emergency situations',
      variables: ['{{project.name}}'],
      actionButtons: [
        {
          id: '1',
          label: 'View Details',
          action: 'navigate',
          value: '/safety/incidents/{{incident.id}}',
          style: 'danger',
        },
        {
          id: '2',
          label: 'Acknowledge',
          action: 'api',
          value: '/api/safety/acknowledge',
          style: 'primary',
        },
      ],
      deliverySettings: {
        channels: ['in-app', 'push', 'email'],
        timing: 'immediate',
        targetAudience: 'all',
        allowDismiss: false,
        soundEnabled: true,
        expiresAfterHours: 1,
      },
      isActive: true,
      isSystem: true,
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-01T00:00:00Z',
      usageCount: 12,
    },
    {
      id: '3',
      name: 'Project Milestone',
      title: 'Milestone Completed',
      message:
        'Congratulations! The milestone "{{milestone.name}}" for {{project.name}} has been completed successfully.',
      category: 'project-updates',
      type: 'success',
      priority: 'medium',
      description: 'Celebration notification for completed milestones',
      variables: ['{{milestone.name}}', '{{project.name}}'],
      actionButtons: [
        {
          id: '1',
          label: 'View Progress',
          action: 'navigate',
          value: '/dashboard/projects/{{project.id}}/milestones',
          style: 'primary',
        },
      ],
      deliverySettings: {
        channels: ['in-app'],
        timing: 'immediate',
        targetAudience: 'role-based',
        allowDismiss: true,
        soundEnabled: false,
        expiresAfterHours: 72,
      },
      isActive: true,
      isSystem: false,
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-01T00:00:00Z',
      usageCount: 156,
    },
  ];

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      // In production, this would fetch from API
      setTemplates(sampleTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
      setMessage({ type: 'error', text: 'Failed to load notification templates' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    setEditMode('create');
    setTemplateData({
      name: '',
      title: '',
      message: '',
      category: '',
      type: 'info',
      priority: 'medium',
      description: '',
      variables: [],
      actionButtons: [],
      deliverySettings: {
        channels: ['in-app'],
        timing: 'immediate',
        targetAudience: 'all',
        allowDismiss: true,
        soundEnabled: false,
        expiresAfterHours: 24,
      },
    });
    setShowEditor(true);
  };

  const handleEditTemplate = (template: NotificationTemplate) => {
    setEditMode('edit');
    setSelectedTemplate(template);
    setTemplateData({
      name: template.name,
      title: template.title,
      message: template.message,
      category: template.category,
      type: template.type,
      priority: template.priority,
      description: template.description || '',
      variables: template.variables,
      actionButtons: template.actionButtons,
      deliverySettings: template.deliverySettings,
    });
    setShowEditor(true);
  };

  const handleSaveTemplate = async () => {
    try {
      // Validate template data
      if (
        !templateData.name ||
        !templateData.title ||
        !templateData.message ||
        !templateData.category
      ) {
        setMessage({ type: 'error', text: 'Please fill in all required fields' });
        return;
      }

      // Extract variables from content
      const titleVariables = extractVariables(templateData.title);
      const messageVariables = extractVariables(templateData.message);
      const allVariables = [...new Set([...titleVariables, ...messageVariables])];

      const templateToSave = {
        ...templateData,
        variables: allVariables,
        id: editMode === 'create' ? Date.now().toString() : selectedTemplate!.id,
        isActive: true,
        isSystem: false,
        createdAt: editMode === 'create' ? new Date().toISOString() : selectedTemplate!.createdAt,
        updatedAt: new Date().toISOString(),
        usageCount: editMode === 'create' ? 0 : selectedTemplate!.usageCount,
      };

      if (editMode === 'create') {
        setTemplates(prev => [...prev, templateToSave as NotificationTemplate]);
      } else {
        setTemplates(prev =>
          prev.map(t => (t.id === templateToSave.id ? (templateToSave as NotificationTemplate) : t))
        );
      }

      setShowEditor(false);
      setMessage({
        type: 'success',
        text: `Template ${editMode === 'create' ? 'created' : 'updated'} successfully!`,
      });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving template:', error);
      setMessage({ type: 'error', text: 'Failed to save template' });
    }
  };

  const handleDeleteTemplate = async (template: NotificationTemplate) => {
    if (template.isSystem) {
      setMessage({ type: 'error', text: 'Cannot delete system templates' });
      return;
    }

    if (!confirm(`Are you sure you want to delete "${template.name}"?`)) return;

    try {
      setTemplates(prev => prev.filter(t => t.id !== template.id));
      setMessage({ type: 'success', text: 'Template deleted successfully' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting template:', error);
      setMessage({ type: 'error', text: 'Failed to delete template' });
    }
  };

  const handleDuplicateTemplate = (template: NotificationTemplate) => {
    const duplicated = {
      ...template,
      id: Date.now().toString(),
      name: `${template.name} (Copy)`,
      isSystem: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      usageCount: 0,
    };
    setTemplates(prev => [...prev, duplicated]);
    setMessage({ type: 'success', text: 'Template duplicated successfully' });
    setTimeout(() => setMessage(null), 3000);
  };

  const extractVariables = (content: string): string[] => {
    const variableRegex = /\{\{([^}]+)\}\}/g;
    const matches = content.match(variableRegex);
    return matches ? [...new Set(matches)] : [];
  };

  const addActionButton = () => {
    const newButton: ActionButton = {
      id: Date.now().toString(),
      label: 'New Action',
      action: 'navigate',
      value: '',
      style: 'primary',
    };
    setTemplateData(prev => ({
      ...prev,
      actionButtons: [...prev.actionButtons, newButton],
    }));
  };

  const updateActionButton = (id: string, field: keyof ActionButton, value: string) => {
    setTemplateData(prev => ({
      ...prev,
      actionButtons: prev.actionButtons.map(button =>
        button.id === id ? { ...button, [field]: value } : button
      ),
    }));
  };

  const removeActionButton = (id: string) => {
    setTemplateData(prev => ({
      ...prev,
      actionButtons: prev.actionButtons.filter(button => button.id !== id),
    }));
  };

  const getTypeIcon = (type: string) => {
    const typeConfig = notificationTypes.find(t => t.value === type);
    if (!typeConfig) return Info;
    return typeConfig.icon;
  };

  const getTypeColor = (type: string) => {
    const typeConfig = notificationTypes.find(t => t.value === type);
    if (!typeConfig) return 'blue';
    return typeConfig.color;
  };

  const getPriorityColor = (priority: string) => {
    const priorityConfig = priorityLevels.find(p => p.value === priority);
    if (!priorityConfig) return 'gray';
    return priorityConfig.color;
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.message.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || template.category === selectedCategory;
    const matchesType = !selectedType || template.type === selectedType;
    return matchesSearch && matchesCategory && matchesType;
  });

  if (loading) {
    return <LoadingSpinner />;
  }

  if (showEditor) {
    return (
      <div className="max-w-7xl mx-auto py-6 px-2">
        {/* Editor Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowEditor(false)}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                {editMode === 'create' ? 'Create' : 'Edit'} Notification Template
              </h1>
              <p className="text-sm text-gray-600">
                Configure notification templates with actions, delivery settings, and targeting
              </p>
            </div>
          </div>

          <button
            onClick={handleSaveTemplate}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
          >
            <Save className="h-4 w-4 mr-2" />
            Save Template
          </button>
        </div>

        {/* Status Message */}
        {message && (
          <div
            className={`rounded-lg p-4 ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}
          >
            <div className="flex items-center">
              {message.type === 'success' ? (
                <CheckCircle className="h-5 w-5 mr-2" />
              ) : (
                <AlertCircle className="h-5 w-5 mr-2" />
              )}
              {message.text}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Template Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Template Information</h3>

              <div className="space-y-4">
                <Input
                  label="Template Name"
                  value={templateData.name}
                  onChange={e => setTemplateData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Task Assignment"
                  required
                />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Dropdown
                    label="Category"
                    options={categories}
                    value={templateData.category}
                    onChange={value => setTemplateData(prev => ({ ...prev, category: value }))}
                    placeholder="Select category"
                    required
                  />

                  <Dropdown
                    label="Type"
                    options={notificationTypes.map(type => ({
                      value: type.value,
                      label: type.label,
                    }))}
                    value={templateData.type}
                    onChange={value => setTemplateData(prev => ({ ...prev, type: value as any }))}
                    required
                  />

                  <Dropdown
                    label="Priority"
                    options={priorityLevels.map(priority => ({
                      value: priority.value,
                      label: priority.label,
                    }))}
                    value={templateData.priority}
                    onChange={value =>
                      setTemplateData(prev => ({ ...prev, priority: value as any }))
                    }
                    required
                  />
                </div>

                <TextArea
                  label="Description"
                  value={templateData.description}
                  onChange={e =>
                    setTemplateData(prev => ({ ...prev, description: e.target.value }))
                  }
                  placeholder="Brief description of when this template is used..."
                  rows={2}
                />
              </div>
            </div>

            {/* Content */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Notification Content</h3>

              <div className="space-y-4">
                <Input
                  label="Notification Title"
                  value={templateData.title}
                  onChange={e => setTemplateData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="New Task Assigned"
                  hint="Title shown in notification header"
                  required
                />

                <TextArea
                  label="Message Content"
                  value={templateData.message}
                  onChange={e => setTemplateData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="You have been assigned a new task..."
                  rows={4}
                  hint="Use variables like {{user.firstName}} to personalize messages"
                  required
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Action Buttons</h3>
                <button
                  onClick={addActionButton}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Button
                </button>
              </div>

              <div className="space-y-4">
                {templateData.actionButtons.map(button => (
                  <div
                    key={button.id}
                    className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg"
                  >
                    <Input
                      label=""
                      value={button.label}
                      onChange={e => updateActionButton(button.id, 'label', e.target.value)}
                      placeholder="Button Label"
                      className="flex-1"
                    />

                    <Dropdown
                      label=""
                      options={[
                        { value: 'navigate', label: 'Navigate' },
                        { value: 'api', label: 'API Call' },
                        { value: 'dismiss', label: 'Dismiss' },
                      ]}
                      value={button.action}
                      onChange={value => updateActionButton(button.id, 'action', value)}
                    />

                    <Input
                      label=""
                      value={button.value}
                      onChange={e => updateActionButton(button.id, 'value', e.target.value)}
                      placeholder={
                        button.action === 'navigate'
                          ? '/path/to/page'
                          : button.action === 'api'
                            ? '/api/endpoint'
                            : ''
                      }
                      className="flex-1"
                    />

                    <Dropdown
                      label=""
                      options={[
                        { value: 'primary', label: 'Primary' },
                        { value: 'secondary', label: 'Secondary' },
                        { value: 'danger', label: 'Danger' },
                      ]}
                      value={button.style}
                      onChange={value => updateActionButton(button.id, 'style', value)}
                    />

                    <button
                      onClick={() => removeActionButton(button.id)}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}

                {templateData.actionButtons.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Bell className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p>No action buttons configured</p>
                  </div>
                )}
              </div>
            </div>

            {/* Delivery Settings */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Delivery Settings</h3>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Delivery Channels
                  </label>
                  <div className="flex space-x-4">
                    {['in-app', 'push', 'email'].map(channel => (
                      <label key={channel} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={templateData.deliverySettings.channels.includes(channel as any)}
                          onChange={e => {
                            const channels = e.target.checked
                              ? [...templateData.deliverySettings.channels, channel as any]
                              : templateData.deliverySettings.channels.filter(c => c !== channel);
                            setTemplateData(prev => ({
                              ...prev,
                              deliverySettings: { ...prev.deliverySettings, channels },
                            }));
                          }}
                          className="rounded border-gray-300 text-red-500 focus:ring-red-400"
                        />
                        <span className="text-sm text-gray-700 capitalize">{channel}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Dropdown
                    label="Target Audience"
                    options={[
                      { value: 'all', label: 'All Users' },
                      { value: 'specific', label: 'Specific Users' },
                      { value: 'role-based', label: 'Role-based' },
                    ]}
                    value={templateData.deliverySettings.targetAudience}
                    onChange={value =>
                      setTemplateData(prev => ({
                        ...prev,
                        deliverySettings: {
                          ...prev.deliverySettings,
                          targetAudience: value as any,
                        },
                      }))
                    }
                  />

                  <Input
                    type="number"
                    label="Expires After (hours)"
                    value={templateData.deliverySettings.expiresAfterHours || ''}
                    onChange={e =>
                      setTemplateData(prev => ({
                        ...prev,
                        deliverySettings: {
                          ...prev.deliverySettings,
                          expiresAfterHours: parseInt(e.target.value) || undefined,
                        },
                      }))
                    }
                    placeholder="24"
                    hint="Hours after which notification expires"
                  />
                </div>

                <div className="flex space-x-6">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={templateData.deliverySettings.allowDismiss}
                      onChange={e =>
                        setTemplateData(prev => ({
                          ...prev,
                          deliverySettings: {
                            ...prev.deliverySettings,
                            allowDismiss: e.target.checked,
                          },
                        }))
                      }
                      className="rounded border-gray-300 text-red-500 focus:ring-red-400"
                    />
                    <span className="text-sm text-gray-700">Allow Dismiss</span>
                  </label>

                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={templateData.deliverySettings.soundEnabled}
                      onChange={e =>
                        setTemplateData(prev => ({
                          ...prev,
                          deliverySettings: {
                            ...prev.deliverySettings,
                            soundEnabled: e.target.checked,
                          },
                        }))
                      }
                      className="rounded border-gray-300 text-red-500 focus:ring-red-400"
                    />
                    <span className="text-sm text-gray-700">Sound Enabled</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Available Variables */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Variables</h3>
              <div className="space-y-2">
                {availableVariables.map(variable => (
                  <div
                    key={variable.name}
                    className="group p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <div className="font-mono text-sm text-red-500">{variable.name}</div>
                    <div className="text-xs text-gray-600">{variable.description}</div>
                    <div className="text-xs text-gray-400">Example: {variable.example}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
              <div className="border rounded-lg p-4 bg-gray-50">
                <div
                  className={`flex items-start space-x-3 p-4 rounded-lg border-l-4 bg-${getTypeColor(templateData.type)}-50 border-${getTypeColor(templateData.type)}-400`}
                >
                  <div className={`flex-shrink-0 text-${getTypeColor(templateData.type)}-600`}>
                    {React.createElement(getTypeIcon(templateData.type), { className: 'h-5 w-5' })}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">
                      {templateData.title || 'Notification Title'}
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {templateData.message || 'Notification message content...'}
                    </p>
                    {templateData.actionButtons.length > 0 && (
                      <div className="flex space-x-2 mt-3">
                        {templateData.actionButtons.map(button => (
                          <button
                            key={button.id}
                            className={`px-3 py-1 text-xs rounded-md ${
                              button.style === 'primary'
                                ? 'bg-red-500 text-white'
                                : button.style === 'danger'
                                  ? 'bg-red-600 text-white'
                                  : 'bg-gray-200 text-gray-700'
                            }`}
                          >
                            {button.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/dashboard/settings')}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Notification Templates</h1>
            <p className="text-sm text-gray-600">
              Configure in-app notification templates with actions, targeting, and delivery settings
            </p>
          </div>
        </div>

        <button
          onClick={handleCreateTemplate}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </button>
      </div>

      {/* Status Message */}
      {message && (
        <div
          className={`rounded-lg p-4 ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          <div className="flex items-center">
            {message.type === 'success' ? (
              <CheckCircle className="h-5 w-5 mr-2" />
            ) : (
              <AlertCircle className="h-5 w-5 mr-2" />
            )}
            {message.text}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>

          <Dropdown
            label=""
            placeholder="All Categories"
            options={[{ value: '', label: 'All Categories' }, ...categories]}
            value={selectedCategory}
            onChange={setSelectedCategory}
          />

          <Dropdown
            label=""
            placeholder="All Types"
            options={[
              { value: '', label: 'All Types' },
              ...notificationTypes.map(type => ({ value: type.value, label: type.label })),
            ]}
            value={selectedType}
            onChange={setSelectedType}
          />

          <div className="flex items-end">
            <span className="text-sm text-gray-600">
              {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredTemplates.map(template => {
            const TypeIcon = getTypeIcon(template.type);
            const typeColor = getTypeColor(template.type);
            const priorityColor = getPriorityColor(template.priority);

            return (
              <div
                key={template.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                      {template.isSystem && (
                        <span className="px-2 py-1 text-xs font-medium bg-brand-100 text-brand-800 rounded-full">
                          System
                        </span>
                      )}
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          template.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {template.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    <div className="flex items-center space-x-2 mb-3">
                      <div
                        className={`flex items-center space-x-1 px-2 py-1 rounded-full bg-${typeColor}-100 text-${typeColor}-800`}
                      >
                        <TypeIcon className="h-3 w-3" />
                        <span className="text-xs font-medium capitalize">{template.type}</span>
                      </div>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full bg-${priorityColor}-100 text-${priorityColor}-800`}
                      >
                        {template.priority}
                      </span>
                    </div>

                    {template.description && (
                      <p className="text-sm text-gray-500 mb-3">{template.description}</p>
                    )}

                    <div className="bg-gray-50 rounded-lg p-3 mb-3">
                      <p className="text-sm font-medium text-gray-900 mb-1">{template.title}</p>
                      <p className="text-sm text-gray-600 line-clamp-2">{template.message}</p>
                    </div>

                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>
                        Category: {categories.find(c => c.value === template.category)?.label}
                      </span>
                      <span>Used: {template.usageCount} times</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEditTemplate(template)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </button>

                    <button
                      onClick={() => handleDuplicateTemplate(template)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Duplicate"
                    >
                      <Copy className="h-4 w-4" />
                    </button>

                    {!template.isSystem && (
                      <button
                        onClick={() => handleDeleteTemplate(template)}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">
                      {template.actionButtons.length} action
                      {template.actionButtons.length !== 1 ? 's' : ''}
                    </span>
                    <div className="flex space-x-1">
                      {template.deliverySettings.channels.map(channel => (
                        <span
                          key={channel}
                          className="w-2 h-2 bg-green-400 rounded-full"
                          title={channel}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredTemplates.length === 0 && (
          <div className="text-center py-12">
            <Bell className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || selectedCategory || selectedType
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by creating your first notification template.'}
            </p>
            <button
              onClick={handleCreateTemplate}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationTemplates;
